import React, { useState, useContext, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../BabysitterSidebar/BabysitterSidebar';
import './BabysitterResetProfilePassword.css';
import { Grid, Box, Container, AppBar, Toolbar } from '@mui/material';
import PasswordInput from '../../../components/Inputs/PasswordInput';
import StyledButton from '../../../components/Button/Button';
import Popup from '../../../components/Inputs/Popup';
import CircularProgress from '@mui/material/CircularProgress';
import { changeProfilePassword } from '../../../services/NetworkServices';
import ApiStatus from '../../../config/apiStatusConstants';
import LeftArrow from '../../../assets/Left 1.svg';
import {
  ProfileContext,
  ProfileContextProps,
} from '../../../context/ProfileContext';
import LogoutPopup from '../../../components/Inputs/LogoutPopup';
import { getStage } from '../../../services/NetworkServices';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import TemporaryDrawer from '../../../components/TemporaryDrawer/TemporaryDrawer';
import './BabysitterResetProfilePassword.css';

const BabysitterResetProfilePassword: React.FC = () => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [showPopUp, setShowPopUp] = useState<boolean>(false);
  const [oldPasswordError, setOldPasswordError] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const [confirmNewPasswordError, setConfirmNewPasswordError] =
    useState<string>('');

  const { showLogoutPopup } = useContext(ProfileContext) as ProfileContextProps;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        console.log('inside use effect');
        const response = await getStage();
        const data = await response.data;
        if (data) {
          console.log('stage is', data.data.stage);
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/supervisor/createProfile');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  const onChangeOldPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setOldPassword(event.target.value);
    setOldPasswordError('');
  };

  const onChangeNewPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setNewPasswordError('');
  };

  const onChangeConfirmNewPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmNewPassword(event.target.value);
    setConfirmNewPasswordError('');
  };

  const handleContinue = () => {
    setShowPopUp(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleUpdatePassword = async () => {
    setApiStatus(ApiStatus.LOADING);
    setOldPasswordError('');
    setNewPasswordError('');
    setConfirmNewPasswordError('');

    const passwordPattern = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

    if (!passwordPattern.test(newPassword)) {
      setNewPasswordError(
        'Password must be at least 8 characters, with upper/lowercase letters and a number',
      );
      setConfirmNewPasswordError(
        'Password must be at least 8 characters, with upper/lowercase letters and a number',
      );
      setApiStatus(ApiStatus.INITIAL);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setConfirmNewPasswordError('Passwords is not match.');
      setApiStatus(ApiStatus.INITIAL);
      return;
    }
    try {
      const response = await changeProfilePassword({
        oldPassword,
        newPassword,
        confirmNewPassword,
      });
      const data = await response.data;
      if (data) {
        setApiStatus(ApiStatus.SUCCESS);
        setShowPopUp(true);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      if (error.response?.data?.message) {
        if (
          error.response?.data?.message.includes(
            'Atleast one uppercase, lowercase, number(0-9), special character',
          )
        ) {
          setNewPasswordError(
            'Password must be at least 8 characters, with upper/lowercase letters and a number',
          );
        } else if (error.response?.data?.message.includes('Invalid password')) {
          setOldPasswordError('Invalid Password.');
        } else if (
          error.response?.data?.message.includes(
            'New password cannot be same as old password',
          )
        ) {
          setNewPasswordError('New password cannot be same as old password');
        }
      } else {
        setApiStatus(ApiStatus.INITIAL);
      }
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton>
          <CircularProgress />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton
          onClick={handleUpdatePassword}
          disabled={!(oldPassword && newPassword && confirmNewPassword)}
        >
          Update Password
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = (errorMsg: string) => {
    return errorMsg ? <p className="error-msg">{errorMsg}</p> : null;
  };

  const handleBackClick = () => {
    navigate('/supervisor/dashboard');
  };

  return (
    <>
      {(showPopUp || showLogoutPopup) && <div className="backdrop"></div>}
      <Box className={`editProfile ${showLogoutPopup ? 'popup-active' : ''}`}>
        <Box className="account-details" sx={{ height: 'auto' }}>
          <Box
            sx={{
              display: { xs: 'block', sm: 'flex' },
              height: { xs: '100vh', sm: '100vh' },
              overflow: { xs: 'auto', sm: 'hidden' },
            }}
          >
            {/* AppBar for xs devices */}
            <AppBar
              position="static"
              sx={{
                display: { xs: 'block', sm: 'none' },
                backgroundColor: 'black',
              }}
            >
              <Toolbar sx={{ background: 'black' }}>
                <TemporaryDrawer />
              </Toolbar>
            </AppBar>

            {/* Sidebar for sm and above */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: { xs: '0%', sm: '35%', md: '28%' },
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  marginLeft: { sm: 2, md: 14 },
                  marginTop: { sm: 2, md: 4 },
                  marginBottom: { sm: 30, md: 4 },
                }}
              >
                <Sidebar />
              </Box>
            </Box>

            {/* Main Content Area */}
            <Box
              sx={{
                width: { xs: '100%', sm: '65%', md: '72%' },
                flexGrow: 1,
                overflow: 'auto',
                height: '100vh',
                paddingY: 10,
              }}
            >
              <Container>
                {/* Back Arrow for xs devices */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <img
                    src={LeftArrow}
                    onClick={handleBackClick}
                    className="arrow-sm"
                  />
                </Box>

                <Box className="edit-profile-pic">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      paddingTop: '20px',
                      width: { xs: '100%', sm: '100%', md: '60%' },
                      gap: '20px',
                    }}
                  >
                    {/* Password Input Fields */}
                    <PasswordInput
                      onChange={onChangeOldPassword}
                      value={oldPassword}
                      label="Old Password"
                    />
                    {renderErrorMsg(oldPasswordError)}
                    <PasswordInput
                      onChange={onChangeNewPassword}
                      value={newPassword}
                      label="New Password"
                    />
                    {renderErrorMsg(newPasswordError)}
                    <PasswordInput
                      onChange={onChangeConfirmNewPassword}
                      value={confirmNewPassword}
                      label="Retype New Password"
                    />
                    {renderErrorMsg(confirmNewPasswordError)}
                    <Grid
                      container
                      sx={{
                        marginTop: '4vh',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {renderButton()}
                    </Grid>

                    <Grid item xs={12}>
                      {showPopUp && (
                        <Popup
                          title={
                            <>
                              Password Reset <br /> Successfully
                            </>
                          }
                          message={
                            <>
                              Your password has been successfully reset. You can
                              now log in with your new password.
                            </>
                          }
                          buttonText="Done"
                          onButtonClick={handleContinue}
                        />
                      )}
                    </Grid>

                    {showLogoutPopup && <LogoutPopup />}
                  </Box>
                </Box>
              </Container>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default BabysitterResetProfilePassword;
